export interface ValidationResult {
  isValid: boolean;
  errorKey?: string;
  errorParams?: Record<string, string | number>;
}

export type FieldValidator = (content: string) => ValidationResult;

export interface FieldValidationRules {
  noVariableAtStartOrEnd?: boolean;
  noLinkAtStartOrEnd?: boolean;
  noOnlyVariables?: boolean;
}

/**
 * Creates a generic field validator from declarative rules.
 * This allows validation to be configured via page-item-config options
 * without writing custom validator functions for each field.
 *
 * @param rules - Declarative validation rules
 * @param channelKey - i18n channel key used for error messages (e.g. 'rcs', 'whatsapp')
 * @returns A validator function compatible with field options.validator
 */
export const createFieldValidator = (rules: FieldValidationRules, channelKey: string): FieldValidator => (content: string): ValidationResult => {
  const cleanContent = content.replace(/<[^>]*>/g, '\n');
  const trimmedContent = cleanContent.trim();

  if (!trimmedContent) {
    return { isValid: true };
  }

  // Only variables check
  if (rules.noOnlyVariables && trimmedContent.match(/^(\{var=[^}]+\}\s*)+$/)) {
    return { isValid: false, errorKey: `templateBuilder.validation.${channelKey}.onlyVariables` };
  }

  // Variable/link position checks
  if (rules.noVariableAtStartOrEnd || rules.noLinkAtStartOrEnd) {
    const variableRegex = /\{var=[^}]+\}/g;
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    const hasVariables = variableRegex.test(trimmedContent);
    const hasLinks = urlRegex.test(trimmedContent);

    if (!hasVariables && !hasLinks) {
      return { isValid: true };
    }

    if (rules.noVariableAtStartOrEnd && hasVariables) {
      if (/^[\s.,;:!?\-–—]*(\{var=[^}]+\})/.test(trimmedContent)) {
        return { isValid: false, errorKey: `templateBuilder.validation.${channelKey}.variableAtStart` };
      }
      if (/(\{var=[^}]+\})[\s.,;:!?\-–—]*$/.test(trimmedContent)) {
        return { isValid: false, errorKey: `templateBuilder.validation.${channelKey}.variableAtEnd` };
      }
    }

    if (rules.noLinkAtStartOrEnd && hasLinks) {
      if (/^[\s.,;:!?\-–—]*((https?:\/\/[^\s]+)|(www\.[^\s]+))/i.test(trimmedContent)) {
        return { isValid: false, errorKey: `templateBuilder.validation.${channelKey}.linkAtStart` };
      }
      if (/((https?:\/\/[^\s]+)|(www\.[^\s]+))[\s.,;:!?\-–—]*$/i.test(trimmedContent)) {
        return { isValid: false, errorKey: `templateBuilder.validation.${channelKey}.linkAtEnd` };
      }
    }
  }

  return { isValid: true };
};

/**
 * Validates that WhatsApp content doesn't have variables or links at the start or end
 * This validation is smart and ignores non-meaningful characters like spaces and punctuation
 * @param content The content to validate
 * @returns Object with isValid boolean and error message
 */
export const validateWhatsappVariablePosition = (content: string): { isValid: boolean; errorKey?: string } => {
  // Remove HTML tags for validation (e.g., <br> tags)
  const cleanContent = content.replace(/<[^>]*>/g, '\n');

  // Trim only leading/trailing whitespace and newlines
  const trimmedContent = cleanContent.trim();

  // If content is empty or only contains variables, it's invalid
  if (!trimmedContent || trimmedContent.match(/^(\{var=[^}]+\}\s*)+$/)) {
    return { isValid: false, errorKey: 'templateBuilder.validation.whatsapp.onlyVariables' };
  }

  // Regex to match variables: {var=...}
  const variableRegex = /\{var=[^}]+\}/g;

  // Regex to match URLs: http://, https://, www.
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;

  // Find all variables in the content
  const variables = trimmedContent.match(variableRegex);

  if (!variables || variables.length === 0) {
    // No variables found, check for links only
    const links = trimmedContent.match(urlRegex);
    if (!links || links.length === 0) {
      return { isValid: true };
    }
  }

  // Check if content starts with a variable
  // We need to check if after removing non-meaningful characters from the start,
  // the content begins with a variable
  const startVariablePattern = /^[\s.,;:!?\-–—]*(\{var=[^}]+\})/;
  const startsWithVariable = startVariablePattern.test(trimmedContent);

  if (startsWithVariable) {
    return { isValid: false, errorKey: 'templateBuilder.validation.whatsapp.variableAtStart' };
  }

  // Check if content starts with a link
  const startLinkPattern = /^[\s.,;:!?\-–—]*((https?:\/\/[^\s]+)|(www\.[^\s]+))/i;
  const startsWithLink = startLinkPattern.test(trimmedContent);

  if (startsWithLink) {
    return { isValid: false, errorKey: 'templateBuilder.validation.whatsapp.linkAtStart' };
  }

  // Check if content ends with a variable
  // We need to check if after removing non-meaningful characters from the end,
  // the content ends with a variable
  const endVariablePattern = /(\{var=[^}]+\})[\s.,;:!?\-–—]*$/;
  const endsWithVariable = endVariablePattern.test(trimmedContent);

  if (endsWithVariable) {
    return { isValid: false, errorKey: 'templateBuilder.validation.whatsapp.variableAtEnd' };
  }

  // Check if content ends with a link
  const endLinkPattern = /((https?:\/\/[^\s]+)|(www\.[^\s]+))[\s.,;:!?\-–—]*$/i;
  const endsWithLink = endLinkPattern.test(trimmedContent);

  if (endsWithLink) {
    return { isValid: false, errorKey: 'templateBuilder.validation.whatsapp.linkAtEnd' };
  }

  return { isValid: true };
};

/**
 * Removes leading/trailing variables and links from content
 * This function automatically fixes the content by removing problematic variables and links
 * @param content The content to fix
 * @returns Fixed content
 */
export const removeLeadingTrailingVariables = (content: string): string => {
  let fixedContent = content;

  // Remove variables at the start (with optional non-meaningful characters before)
  fixedContent = fixedContent.replace(/^[\s.,;:!?\-–—]*\{var=[^}]+\}[\s.,;:!?\-–—]*/g, '');

  // Remove variables at the end (with optional non-meaningful characters after)
  fixedContent = fixedContent.replace(/[\s.,;:!?\-–—]*\{var=[^}]+\}[\s.,;:!?\-–—]*$/g, '');

  // Remove links at the start (with optional non-meaningful characters before)
  fixedContent = fixedContent.replace(/^[\s.,;:!?\-–—]*((https?:\/\/[^\s]+)|(www\.[^\s]+))[\s.,;:!?\-–—]*/gi, '');

  // Remove links at the end (with optional non-meaningful characters after)
  fixedContent = fixedContent.replace(/[\s.,;:!?\-–—]*((https?:\/\/[^\s]+)|(www\.[^\s]+))[\s.,;:!?\-–—]*$/gi, '');

  return fixedContent.trim();
};
